import moment from 'moment'
import { clone, orderBy, snakeCase } from 'lodash'
import { PLAYER_ACTIONS } from '@scoutgg/scoring-rules/src/sports/football/fantasy-bet-builder'
import { Template, Component, Attribute } from '@scoutgg/ftw/source/lib'
import notify from '@scoutgg/ftw/source/services/notification'
import '@scoutgg/ftw/source/atoms/button/button'
import '@scoutgg/ftw/source/atoms/slider/slider'
import '@scoutgg/ftw/source/atoms/pagination/pagination'
import '@scoutgg/ftw/source/atoms/toggle/toggle'
import '@scoutgg/ftw/source/atoms/dropdown/dropdown'
import sources from '../../../helpers/sources'
import query from '../../services/query'
import pushState from '../../services/state'
import { used, sortPlayers } from '../../services/positions'
import template from './prices.jade'
import statuses from '../../services/statuses'
import { Season, Team, Price, Match, Player } from '../../models'
import normalize from '../../../helpers/normalize'
import evaluation from '../../../helpers/evaluate'
import statsFields from './stats-fields'
import { computeExpectedGoals, computeOdds } from './expected-goals'
import checkDuplicates from '../../../helpers/duplicate-names'
import emitter from '@scoutgg/ftw/source/services/emitter'
import '../seasons'
import '../form-field'

const SPORTS_WITH_3PARTY_XG = ['baseball', 'basket']
const DISPLAY_OLD_SEASONS = true
const feedPrices = ['baseball']
const minimumPrice = { outfielder: 4, infielder: 4, pitcher: 8 }
const defaultPrice = { outfielder: 5, infielder: 5, pitcher: 8 }

const getGameweekObject = v => ({label: `GW:${v}`, value: v})

@Component('sporty')
@Template(template)
@Attribute('season', Season)
@Attribute('team', Team)
@Attribute('match', Match)
@Attribute('sport', String)
@Attribute('available', Boolean)
@Attribute('show-alternate', Boolean)
@Attribute('show-remarks', Boolean)
export default class Prices extends HTMLElement {
  PLAYER_ACTIONS = PLAYER_ACTIONS
  playerActionPoints = {}

  created() {
    this.pages = {}
  }

  attached() {
    window.addEventListener('popstate', ev => {
      this.season = (ev.state && ev.state.season) || this.season
      this.team = (ev.state && ev.state.team) || null
      this.match = (ev.state && ev.state.match) || null
    })
    this.approvedLeagues = ['PreSeasonNFL 2019', 'PreSeasonGames NFL 2019']
    this.pricedTeams = []
    this.team = query().team
    this.match = query().match
    this.gameweeks = []
    this.convertionXfpRatio = 1
    this.xfpQuantile = 0.9
    this.pickedGameweek = query().gameweek && getGameweekObject(query().gameweek)
    this.xPriceConversionFactor = 1000
    this.xPriceConversionName = `custom-${this.xPriceConversionFactor}`

    this.update(Season.load())
    if(this.season) this.reload()
    this.mods = [0, 0]
    this.modPosition = null
    this.limit = 100
    this.pricesConf = {
      expectedOdds: [1, 1],
      averagePlayerPrice: 8.3,
      averageFactor: null,
    }
    this.xGoalValues = {
      over: 2,
      under: 2,
      score: 2.5,
      home: 3,
      away: 3,
      draw: 3,
    }
    this.xG = [
      { home: 1.75 },
      { away: 1.75 },
    ]
    this.statsFields = statsFields
    this.xGoalResult = false
    this.upload = false
  }

  togglePlayerPointsView(team) {
    team.completedPlayers = team.completedPlayers.map(player => {
      player.actionPoints = this.playerActionPoints[player._id]
        ? { ...this.playerActionPoints[player._id] }
        : PLAYER_ACTIONS.reduce((acc, key) => ({ ...acc, [key]: 0 }), {})

      return player
    })

    team.isPlayerPointsView = !team.isPlayerPointsView
    this.render()
  }

  async savePlayerPoints(team) {
    const payload = team.completedPlayers
      .filter(el => Object.values(el.actionPoints).some(points => Number(points) > 0))
      .map(player => ({
        player: player._id,
        actions: Object.entries(player.actionPoints).map(([actionType, points]) => ({
          actionType,
          points: Number(points),
        }))
      }))

    if (!payload.length) {
      return notify('No points for any player were set', { level: 'danger', timeout: 3000 })
    }

    const incompletePoints = payload.filter(el => el.actions.some(a => a.points < 1))

    if (incompletePoints.length) {
      return notify('Points for some players are incomplete', { level: 'danger', timeout: 3000 })
    }

    await this.match.post('action-points', {
      entries: payload
    })
    this.playerActionPoints = await this.match.load('action-points')

    this.togglePlayerPointsView(team)
    notify('Successfuly saved player action points', { level: 'success', timeout: 3000 })
  }

  async seasonChange() {
    this.team = null
    this.match = null
    this.modPosition = null

    this.reload()
    this.changeState()
    this.render()
  }

  changeSeason({season}) {
    this.season = season
  }

  changeSport(sport) {
    this.sport = sport || null
    this.changeState()
    this.render()
  }

  async changeGameweek(gameweek) {
    this.pickedGameweek = gameweek
    this.changeState()

    await this.attemptToLoadPriced()

    this.render()
  }

  async changePriceScope(scope) {
    this.priceScope = scope
    this.changeState()

    this.render()
  }

  async attemptToLoadPriced() {
    if (this.team?._id === 'priced-players' && !isNaN(this.pickedGameweek?.value)) {
      const { priced } = await Price.load('priced', {
        season: this.season?._id,
        round: this.pickedGameweek?.value,
        sport: this.season.sport,
      })

      const {pricedCloneMap, pricedObjects} = priced.reduce((acc, p) => {
        p.player = Player.create(p)

        acc.pricedObjects.push(p)
        const cloned = clone(p)
        const key = this.getPriceKey(cloned)
        acc.pricedCloneMap.set(key, cloned)
        return acc
      }, {pricedCloneMap: new Map(), pricedObjects: []})

      this.unchangedClones = pricedCloneMap
      this.priced = pricedObjects
    }
  }

  async changeTeam(team) {
    this.team = team || null
    this.match = null
    this.changeState()

    await this.attemptToLoadPriced()

    this.render()
  }

  getPriceKey (price) {
    return `${price._id}-${price.match}`
  }

  changeRound(round) {
    this.round = round || null
    this.changeState()
  }

  async changeMatch(match) {
    if(this.$changeMatch) {
      this.$changeMatch = match
      return
    }
    this.$changeMatch = true
    this.render()
    this.match = match
    {
      const data = await this.match.load()
      delete data.user // Overwrites user from detailed Season
      this.update(data)
    }

    for(let team of this.match.teams) {
      const teamMatches = (this.realMatches || [])
        .filter(m => m.round > 0 && m.teams && m.teams.includes(team))
      const matchForTeamIndex = teamMatches.indexOf(this.match)
    }
    var teamsparams = Object.assign({
      sport: this.season.sport,
      teams: this.match.teamIds,
    }, [ 'baseball', 'basket' ].includes(this.season.sport) && DISPLAY_OLD_SEASONS ? {
      league: this.season.league._id,
    } : {
      season: this.season._id,
    })
    const [
      { prices },
      data,
      playerActionPoints,
    ] = await Promise.all([
      Match.load('prices', {
        season: this.season._id,
        match: this.match._id,
      }),
      Match.load('match-stats', teamsparams),
      this.match.load('action-points')
    ])

    for(const price of prices) {
      this.prices[price.player] = price
      if(!price.matchPrices) price.matchPrices = {}
      if(!price.matchPrices[this.match]) {
        price.matchPrices[this.match] = {
          factor: 0,
          price: 0,
        }
      }
    }

    const projections = {}
    const lineups = this.match?.lineups || this.lineups || []
    for(const l of lineups) {
      if(!l.fantasyProjection) continue
      projections[l.player] = l.fantasyProjection
    }
    data.projections = projections

    this.playerActionPoints = playerActionPoints
    this.seasonMatches = data
    this._loadPlayerStats(data)


    if(this.season?.league?.sport === 'hockey') {
      this.projectedPrices = await Match.load('projected-prices', {
        expected_ratio: (this.match.expectedGoals[0] || 1) /
          (this.match.expectedGoals[1] || 1),
        team_home: this.match.teams[0].toString(),
        team_away: this.match.teams[1].toString(),
        league: this.season.league.name
      })
    }
    this.xGoalValues = this.match.odds.over.hasOwnProperty('default')
      ? {over: 2, under: 2, score: 2.5, home: 3, away: 3, draw: 3}
      : this.match.odds
    this.recommendedBudget = this.recommendBudget(this.prices, this.match._id)
    this.changeState()

    const nextMatch = this.$changeMatch
    this.$changeMatch = null
    if(nextMatch !== true) return this.changeMatch(nextMatch)
    this.render()
  }

  _loadPlayerStats(data) {
    for(let team of this.listedTeams) {
      team.completedPlayers = this.teamPlayers(team).map(player => {
        player = Player.create(Object.create(player))
        if(evaluation[this.season.sport] &&
           evaluation[this.season.sport].processPlayerStats) {
          player.stats = evaluation[this.season.sport]
            .processPlayerStats(data.matchStats[player._id], data.projections[player._id])
        } else {
          player.stats = data.matchStats[player._id]
        }
        return player
      })
      if(evaluation[this.season.sport] &&
         evaluation[this.season.sport].processLineup) {
        team.completedPlayers = evaluation[this.season.sport]
          .processLineup(team.completedPlayers, this.prices)
      }
    }
  }

  changeState() {
    pushState({
      team: this.team && this.team._id,
      season: this.season && this.season._id,
      match: this.match && this.match._id,
      gameweek: this.pickedGameweek?.value
    })
    this.render()
  }

  changePage(team, page) {
    this.pages[team] = page
    this.render()
    setTimeout(() => this.render(), 200)
  }

  toggleAvailable() {
    this.available = !this.available
    this.render()
    setTimeout(() => this.render(), 200)
  }

  sortBy(category) {
    this.reverse = (this.sort || 'name') === category ? !this.reverse : false
    this.sort = category
    this.render()
    setTimeout(() => this.render(), 200)
  }

  categoryIcon(column, sortBy, reverse) {
    if(sortBy !== column) {
      return 'ui--table_sort'
    } else if(reverse) {
      return 'ui--table_sorted_up'
    } else {
      return 'ui--table_sorted_down'
    }
  }

  getStat(player, cfg, defaultValue = 0) {
    let stat = player.stats?.[cfg.key]
    if(stat == null) stat = defaultValue
    if(cfg.to_fixed) return (+stat).toFixed(2)
    return stat
  }

  recommendBudget(prices, match) {
    const sortedPrices = this.listedTeams
      .map(t => t.players)
      .reduce((acc, t) => acc.concat(t), [])
      .map(p => parseInt(prices[p._id]?.prices[match]?.price))
      .filter(p => p)
      .sort((p1, p2) => p2 - p1)
    const maxPrice = sortedPrices.slice(0, 5).reduce((acc, e) => acc + e, 0)
    const minPrice = sortedPrices.slice(10, 5).reduce((acc, e) => acc + e, 0)
    return .8 * (minPrice + maxPrice)
  }

  async reload() {
    if(!this.season) return
    if(this.$reload) {
      this.$reload = 'repeat'
      return
    }
    this.$reload = true
    this.render()
    try {
      var params = {
        $reload: true,
        season: this.season._id,
      }
      this.prices = {}
      const [
        { unpriced },
        data,
      ] = await Promise.all([
        (!this.match && Price.load('unpriced', params) || { unpriced: [] }),
        Season.load('detailed', params),
      ])

      const { rounds } = await Season.load('game-weeks', {
        season: this.season._id,
      })
      this.gameweeks = rounds.map(getGameweekObject)

      await this.attemptToLoadPriced();

      this.unpriced = unpriced.slice(0, this.limit)
        .map(p => Player.create(Object.create(p)))

      this.players = data.players
      this.realMatches = data.matches.sort((a, b) => {
        return moment(a.startTime).diff(b.startTime)
      })
      if(!this.match) {
        this.match = this.matches
          .find(m => moment(m.startTime).fromNow() > 0) || this.matches[0]
      } else {
        this.changeMatch(this.match)
      }


      const pricedPlayersTeam = Team.create({_id: "priced-players", name: "Priced players"})

      this.teams = [pricedPlayersTeam, ...data.teams]
        .filter(t => !t.notAvailable)
      this.user = data.user
      emitter.emit('match-updated')
    } catch (err) {
      console.error(err)
      notify(err.message, {
        level: 'danger',
        timeout: 5000,
      })
    } finally {
      if(this.$reload === 'repeat') {
        this.$reload = null
        return this.reload()
      }
      this.$reload = null
      this.render()
    }
  }

  get matches() {
    return (this.realMatches || [])
      .filter(m => {
        const validMatch = m.round > 0 && m.teams
        const teamIncludes = m.teams.includes(this.team)

        return validMatch && teamIncludes
      })
  }

  filteredPlayers(team) {
    var search = (this.search || '').split(/\s+/).map(normalize)
    let players = team.completedPlayers || this.teamPlayers(team) || []
    if(this.match.sport === 'golf') {
      const lineups = this.match?.lineups || this.lineups || []
      const ids = lineups.map(l => l.playerId)
      players = players.filter(p => ids.includes(p._id))
    }
    return players
      .filter(p => !this.modPosition || this.modPosition === p.position)
      .filter(player => {
        if(this.available && (this.prices[player] || {}).disabled) return false
        return search.length === search.filter(piece => {
          return +player.shirt === +piece ||
            ~normalize(player.firstName).indexOf(piece) ||
            ~normalize(player.lastName).indexOf(piece) ||
            ~normalize(player.customName).indexOf(piece) ||
            ~normalize(player.position).indexOf(piece) ||
            ~normalize(player.team && player.team.name).indexOf(piece)
        }).length
      })
  }

  sortPriced() {
    const order = this.reverse ? 'asc' : 'desc'
    const propertyToSort = this.sort === 'team' ? 'team.name' : this.sort || 'name'

    let sortKeyPath;
    switch(propertyToSort) {
      case('factor'):
      case('seasonPrice'):
      case('price'):
      case('round'):
        sortKeyPath = propertyToSort
        break;
      default:
        sortKeyPath = `player.${propertyToSort}`
    }

    this.priced = orderBy(this.priced, [sortKeyPath], order)

    return this.priced
  }

  sortedPlayers(team) {
    var page = this.pages[team] || 0
    var limit = this.limit
    var offset = page * limit
    const modifier = this.reverse ? -1 : 1
    const sort = (a, b) => {
      let diff
      switch(this.sort) {
        case 'seasonPrice':
          diff =
            this.prices[a]?.seasonPrice -
            this.prices[b]?.seasonPrice
          return modifier * (diff || sortPlayers(a, b, 'prices'))
        case 'gameweekPrice':
          diff =
            (this.prices[a]?.prices[this.match]?.price || 0) -
            (this.prices[b]?.prices[this.match]?.price || 0)
          return modifier * (diff || sortPlayers(a, b, 'prices'))
        case 'xprice':
          diff =
            (this.priceScope.prices[a] || 0) -
            (this.priceScope.prices[b] || 0)
          return modifier * (diff || sortPlayers(a, b, 'prices'))
        case 'factor':
          diff =
            (this.prices[a]?.prices[this.match]?.factor || 0) -
            (this.prices[b]?.prices[this.match]?.factor || 0)
          return modifier * (diff || sortPlayers(a, b, 'prices'))
        case 'shirt':
          // Keep players with no shirt number at the end of the list
          const coalesce = this.reverse ? 0 : 9999
          diff = (a.shirt || coalesce) - (b.shirt || coalesce)
          return modifier * (diff || sortPlayers(a, b, 'prices'))
        case 'total_minutes':
        case 'appearances':
        case 'lineups':
        case 'benches':
        case 'goals':
        case 'assists':
        case 'minutes':
        case 'goals_and_assists':
        case 'goals_and_assists_mean':
        case 'minutes_in_last_5':
        case 'average_minutes':
        case 'fp_per_minute':
        case 'projected_fp':
        case 'ops':
        case 'fantasy_points':
        case 'era':
        case 'home_runs':
        case 'pitcher_wins':
        case 'innings':
        case 'strikeouts':
        case 'form':
        case 'proj_intelligentRank':
        case 'proj_value':
        case 'proj_intelligentScore':
          diff = (a.stats?.[this.sort] || 0) - (b.stats?.[this.sort] || 0)
          return modifier * (diff || sortPlayers(a, b, 'prices'))
        default:
          return modifier * (sortPlayers(a, b, 'prices'))
      }
    }

    return this.filteredPlayers(team)
      .sort(sort)
      .slice(offset, limit + offset)
  }

  updateFilter(e) {
    this.search = e.target.value
    this.page = 0
    this.render()
  }

  initializeStatus(player) {
    var price = this.prices[player]
    if(!price.statusDuration) price.statusDuration = []
    var rounds = (this.rounds || []).filter(r => r)
    if(!price.statusDuration[0]) {
      price.statusDuration[0] = Math.min(...rounds) || 0
    }
    if(!price.statusDuration[1]) {
      price.statusDuration[1] = Math.max(...rounds) || 1000
    }
  }

  priceFor(player) {
    const p = (this.prices[player]) || (
      this.prices[player] = Price.create({
        seasonPrice: 0,
        prices: [],
        player: player,
        season: this.season,
        teamId: player.team?._id,
      })
    )
    if(!p.team) {
      this.prices[player].teamId = player.team?._id
      p.team = player.team?._id
    }
    return p
  }
  renderStats(value, player = { stats: {} }) {
    player.stats.average_minutes = value
    player.stats.projected_fp = evaluation[this.season.league.sport].projectFPs(player.stats)
    this.render()
  }
  togglePriced(priced){
    priced.disabled = !priced.disabled
  }

  isAllDisabled(team) {
    return this.sortedPlayers(team).every(p => this.priceFor(p)?.disabled)
  }
  toggleAllPrices(team){
    const allDisabled = this.isAllDisabled(team)
    for(const p of this.sortedPlayers(team)) {
      this.priceFor(p).disabled = !allDisabled
    }
    this.render()
  }

  async xFPtoPrice(){
    const result = await Price.load('xfp-to-price', {
      match: this.match._id,
      quantile: this.xfpQuantile,
      ratio: this.convertionXfpRatio,
    })

    Object.entries(result)
      .forEach(([key, value]) => {
        this.prices[key].prices[this.match._id].price = value.toFixed(1)
      })

    this.render()
  }

  changeScopePrice(player, price) {
    if(!this.priceScope) {
      return
    }
    if(!this.priceScope.prices) {
      return
    }
    this.priceScope.prices[player._id] = price
    if(!this.priceScope.$changed) {
      this.priceScope.$changed = {}
    }
    this.priceScope.$changed[player._id] = price
  }

  async save() {
    if(this.match) this.match.odds = this.xGoalValues
    const data = {
      season: this.season,
      match: this.match,
      prices: [],
    }
    if(this.match) {
      this.match.teams.forEach(team => {
        this.teamPlayers(team).forEach(player => {
          data.prices.push(this.prices[player])
        })
      })
    } else {
      data.prices = this.unpriced
        .map(player => this.prices[player])
    }
    try {
      if (this.team && this.team._id ==='priced-players') {

        const newPrices = this.priced.reduce((acc, p) => {
          const key = this.getPriceKey(p)
          const clonedPrice = this.unchangedClones.get(key)

          const wasPriceChanged = p.seasonPrice !== clonedPrice.seasonPrice
            || p.factor !== clonedPrice.factor
            || p.price !== clonedPrice.price
            || p.disabled !== clonedPrice.disabled
          if (wasPriceChanged) {
            const priceObj = {
              _id: p.priceId,
              playerId: p._id,
              seasonId: p.season,
              seasonPrice: p.seasonPrice,
              disabled: p.disabled,
              teamId: p.team._id,
            }
            priceObj.matchPrices = {}
            priceObj.matchPrices[p.match] = { factor: p.factor, price: p.price }

            acc.push(Price.create(priceObj))
          }

          return acc
        }, [])

        data.prices = newPrices
      }

      if(this.priceScope?.$changed) {
        data.xprices = [{
          match: this.match._id,
          scope: this.priceScope.scope,
          prices: this.priceScope.$changed,
        }]
      }

      const checked = data.prices
        .filter(p => p)
        .filter(p => !p.disabled)
        .map(p => p.player)

      await checkDuplicates(checked)
      await Price.post(data)

      if(this.priceScope) {
        delete this.priceScope.$changed
      }

      var message = this.match
        ? `Prices saved for ${this.match.descriptor}`
        : 'Prices saved for unpriced players'
      notify(message, {
        level: 'success',
        timeout: 5000,
      })

    } catch(err) {
      console.error(err)
      if(err.message) notify(err.message, {
        level: 'danger',
        timeout: 10000,
      })

    }
  }

  send(season, type, match) {
    if(season.$isSubmitting) return
    season.$isSubmtting = true
    const args = {}
    if(match) args.match = match._id
    season.post(`send-${type}`, args).then(() => {
      notify(`Sending of ${type} is completed`, {
        level: 'success',
        timeout: 5000,
      })
    }).catch(err => {
      notify(err.message, { level: 'danger', timeout: 1000 })
    }).then(() => {
      season.$isSubmitting = false
      this.render()
    })
    this.render()
  }

  makeFactor(n) {
    var val = Object.values(n)[0] / (this.season.averageGoals || 0) || 0
    if(val === Infinity) val = 1
    return val
  }

  makePrice({ player, price, match, side, factors }) {
    if(this.projectedPrices && this.projectedPrices[player._id]) {
      return this.projectedPrices[player._id].toFixed(1)
    }
    const matchPrice = price.prices[match]
    return ((
      matchPrice.factor *
      this.season.averagePrice *
      (player.position === 'goalkeeper'
        ? 1 / (factors[1 - side] || 1)
        : factors[side] || 1
      )
    ) || 0).toFixed(1)
  }

  round(value, precision) {
    var multiplier = Math.pow(10, precision || 0);
    return Math.round(value * multiplier) / multiplier;
  }

  generatePricesFromFeed(opts = {}) {
    const match = this.match
    const fantasyDataMap =  (this.match.lineups || [])
      .reduce((acc, lineup) => {

        acc[lineup.playerId] = {xFP: lineup.xFP}

        return acc
      }, {})
    for(const team of this.listedTeams) {
      const playerInfo = this.sortedPlayers(team)
      const pMap = {}
      for(const player of playerInfo) {
        pMap[player._id] = player
      }
      team.completedPlayers.forEach(player => {
        const price = this.prices[player]
        if(!price) return
        if(price && price.disabled) return
        const prices = price.matchPrices || {}
        if(opts.freshOnly && prices[match]) return
        if(!prices[match]) prices[match] = {factor: 0}
        let newPrice
        if(!fantasyDataMap[player._id] || !fantasyDataMap[player._id].xFP) {
          newPrice = this.round(pMap[player._id].stats.fantasy_points * 1.15,1)
        } else {
          newPrice = this.round(fantasyDataMap[player._id].xFP * 1.15,1)
        }
        prices[match].price = Math.max(newPrice, minimumPrice[player.position]) || defaultPrice[player.position]

      })
      this.render()
    }
  }

  xPriceConvert(match, factor, scope) {
    let priceScope = match.xprices?.find(xp => xp.scope === scope)
    if(!priceScope) {
      priceScope = {
        scope,
        prices: {},
      }
      if(match.xprices) {
        match.xprices.push(priceScope)
      } else {
        match.xprices = [priceScope]
      }
    }
    if(!priceScope.prices) {
      priceScope.prices = {}
    }
    const { prices } = priceScope
    for(const [id, price] of Object.entries(this.prices)) {
      const matchPrice = price.matchPrices[match]
      if(!matchPrice?.price) {
        continue
      }
      const converted = +(matchPrice.price * factor).toFixed(1)
      if(converted === prices[id]) {
        continue
      }
      if(!priceScope.$changed) {
        priceScope.$changed = {}
      }
      priceScope.$changed[id] = converted
      prices[id] = converted
    }
    this.render()
  }

  xPriceReverse(match, factor, scope) {
    if(!factor || factor <= 0) {
      notify('Multiplier must be greater than 0', { level: 'danger', timeout: 5000 })
      return
    }
    const priceScope = match.xprices?.find(xp => xp.scope === scope)
    if(!priceScope?.prices) {
      notify(`No XPrice scope "${scope}" found for this match`, { level: 'danger', timeout: 5000 })
      return
    }
    for(const [id, xprice] of Object.entries(priceScope.prices)) {
      if(!xprice) continue
      const price = this.prices[id]
      if(!price) continue
      if(!price.matchPrices) price.matchPrices = {}
      if(!price.matchPrices[match]) {
        price.matchPrices[match] = { factor: 0, price: 0 }
      }
      price.matchPrices[match].price = +(xprice / factor).toFixed(1)
    }
    this.render()
  }

  get hasFeedPrices() {
    return feedPrices.includes(this.season.sport)
  }
  generate(team, opts = {}) {
    const match = this.match
    const league = this.leagues.find(el => el._id === this.season.leagueId)
    var factors = this.getXGoalResult
      .map(this.makeFactor.bind(this)) || 0

    const fantasyDataMap =  (this.match.lineups || [])
      .reduce((acc, lineup) => {
        const normalizedProjection = Object.entries(lineup.fantasyProjection || {})
          .reduce((acc, [key, entry]) => {
            acc[snakeCase(key)] = entry
            return acc
          }, {})

        acc[lineup.playerId] = {xFP: lineup.xFP, ...normalizedProjection}

        return acc
      }, {})

    var side = +(team === match.awayTeam)
    team.completedPlayers.forEach(player => {
      const price = this.prices[player]
      if(!price) return
      if(price && price.disabled) return
      const prices = price.matchPrices || {}
      if(opts.freshOnly && prices[match]) return
      if(!prices[match]) prices[match] = {factor: 0}
      const expected = this.xGoalValues

      if(evaluation[this.season.sport] && evaluation[this.season.sport].makePrice) {
        const fantasyXFP = fantasyDataMap[player._id]
        prices[match].price = evaluation[this.season.sport].makePrice({
          player,
          price,
          match,
          side,
          factors,
          league,
          expected,
          fantasyXFP,
        })
      } else {
        prices[match].price = this.makePrice({
          player,
          price,
          match,
          side,
          factors,
        })
      }
    })
    this.render()
  }
  setAllMatchPrices(team, position, defaultPrice) {
    const match = this.match
    this.teamPlayers(team)
      .filter(p => !position || p.position === position)
      .forEach(player => {
        const prices = this.prices[player].matchPrices
        const price = prices[match] || {}
        price.price = defaultPrice
        if(!price.price) return
        price.price = +(price.price)
      })
    this.render()
  }
  modify(team, position, factor) {
    factor = 1 + (factor / 100)
    const match = this.match
    this.teamPlayers(team)
      .filter(p => !position || p.position === position)
      .forEach(player => {
        const prices = this.prices[player].matchPrices
        const price = prices[match]
        if(!price) return
        price.price = +(price.price * factor).toFixed(1)
      })
    this.render()
  }

  isFactorCopy(season) {
    if([
      'hockey',
      'basket',
    ].includes(season.sport)) {
      return false
    }
    if(season.isDuel) return true
    return false
  }

  copy(team, attr) {
    const {
      startTime,
      round,
    } = this.match
    let matches

    if(this.isOneRound) {
      matches = this.realMatches
        .filter(m => m.round === round && m.matchType !== 'one_round')
    } else {
      matches = (this.realMatches || [])
        .filter(m => m.startTime <= startTime)
        .sort((a, b) => {
          const aTime = +a.startTime
          const bTime = +b.startTime
          if(aTime !== bTime) return bTime - aTime
          const aBout = a.bout || 0
          const bBout = b.bout || 0
          if(aBout !== bBout) return bBout - aBout
          if(b._id > a._id) return -1
          return 1
        })
    }

    for(const player of this.teamPlayers(team)) {
      const price = this.prices[player]
      if(!price) continue
      const prices = price.matchPrices
      if(price.prevMatch && prices[price.prevMatch]) {
        prices[this.match][attr] = prices[price.prevMatch][attr]
        continue
      }
      for(const m of matches) {
        if(!prices[m]) continue
        if(!prices[m][attr]) continue
        prices[this.match][attr] = prices[m][attr]
        break
      }
    }
    this.render()
  }

  generateUnpriced() {
    this.unpriced.forEach(player => {
      var price = this.prices[player]
      var match = this.nextMatch[player.team]
      if(!price || !match) return
      var side = +(player.team === match.awayTeam)
      var factors = this.getXGoalResult.map(this.makeFactor.bind(this))
      price.prices[match] =
        this.makePrice({player, price, match, side, factors})
    })
    this.render()
  }

  togglePrice(price, e) {
    price.disabled = !price.disabled
    // this._loadPlayerStats(this.seasonMatches)
    // this.render()
  }

  collectionAverage(collection = []) {
    const sum = collection.reduce((initial, value) =>
    (initial += value), 0)
    return (sum / collection.length)
  }
  averageTeamFactor({players = []}, attr) {
    const factors = players.map(player => {
      const prices = this.prices[player]
      if(!(prices && prices.matchPrices)) return
      const price = prices.matchPrices[this.match]
      return (price && price[attr]) || 1
    })
    return (this.collectionAverage(factors) || 1).toFixed(3)
  }

  averageSeasonFactor() {
    const teams = this.teams || []
    const factorsSum = teams.reduce((initial, team) => {
      initial += Number(this.averageTeamFactor(team, 'factor'))
      return initial
    }, 0)
    return factorsSum / teams.length
  }

  calculatePlayerPrice(odd = 1, playerFactor = 1, defaultLeaguePrice = 1,
    averageLeagueFactor = 1, averageTeamFactor = 1) {
    const percentagePerspective = (1 / odd) * 100 - 50

    const tenthFactorPrice = averageLeagueFactor / defaultLeaguePrice

    const perspectiveDifference = (defaultLeaguePrice * percentagePerspective) / 100

    const beforeFactorPrice = defaultLeaguePrice + perspectiveDifference

    const factorDifference = playerFactor - averageTeamFactor

    const playerPrice = beforeFactorPrice + (factorDifference * tenthFactorPrice * 100)

    return playerPrice.toFixed(1)
  }

  canEditPrice(user) {
    return true
  }

  validate() {
    var value = this.xGoalValues.score / 0.5
    var reminder = value % 2
    var isEven =(reminder == 0) ? true : false
    var isInteger = Number.isInteger(parseFloat(reminder))
    if (isEven && isInteger) {
      this.xGoalValues.score = parseFloat(this.xGoalValues.score) + 0.5
      this.render()
    }
    else if(!isInteger){
      this.xGoalValues.score = Math.round(this.xGoalValues.score)
      this.validate()
    }
  }

  isUploading() {
    this.upload = !this.upload
    this.render()
  }

  get isOneRound() {
    if(!this.season || !this.match) {
      return false
    }
    const { sport } = this.season
    const { matchType } = this.match
    return sport === 'golf' && matchType === 'one_round'
  }

  teamPlayers(team) {
    let players = this.players || []
    if(this.isOneRound) {
      const lineups = this.match?.lineups || this.lineups || []
      players = lineups
        .filter(l => l.status === 'expected')
        .map(l => l.player)
    }
    players = this.players.filter(p => p.team === team)
    return players
  }

  get matchStatuses() {
    let matches = this.matches
    var newArr = [],
        statuses = {},
        cur
    for(const cur of matches) {
      if (!(cur.status in statuses)) {
        statuses[cur.status] = {status: cur.status, sortedMatches: []}
        newArr.push(statuses[cur.status])
      }
      statuses[cur.status].sortedMatches.push(cur)
    }
    return newArr
  }

  get listSports() {
    this.activeSeasons = this.seasons.filter((season)=> season.active)
    return Array.from(new Set(this.activeSeasons.map(season => season.sport)))
      .sort()
  }

  get computing(){
    return computeExpectedGoals(
      this.xGoalValues.over || 2,
      this.xGoalValues.under || 2,
      this.xGoalValues.score || 2.5,
      this.xGoalValues.home || 3,
      this.xGoalValues.draw || 3,
      this.xGoalValues.away || 3,
    )
  }

  get getXGoalResult() {
    if(SPORTS_WITH_3PARTY_XG.includes(this.season.sport)) {
      return [
        {home: this.match.expectedGoals[0]},
        {away: this.match.expectedGoals[1]},
      ]
    }

    let xG = this.computing
    this.xG = [
      {home:xG.xgHome.toFixed(2)},
      {away:xG.xgAway.toFixed(2)},
    ]
    this.match.expectedGoals[0] = this.xG[0].home
    this.match.expectedGoals[1] = this.xG[1].away
    this.render()
    return this.xG
  }

  get getOddsResult(){
    if(SPORTS_WITH_3PARTY_XG.includes(this.season.sport)) return

    let expOdds = computeOdds(this.match.expectedGoals[0]
    || this.xG[0].home, this.match.expectedGoals[1]
    || this.xG[1].away, this.xGoalValues.score)
    this.expOdds = expOdds
    for (var key in this.xGoalValues){
      if (key!= 'score' && ['over','under'].includes(key))
        this.xGoalValues[key] = (1 / (this.expOdds[key])).toFixed(2)
      else if(key!= 'score' && ['home','away', 'draw'].includes(key)){
        this.xGoalValues[key] = (1 / (this.expOdds[key])).toFixed(2)
      }
    }
    this.render()
  }

  get statuses() {
    return statuses
  }

  get sports() {
    return sports
  }

  get positions() {
    return used(this.players || [])
  }

  get getMatch() {
    return this.match
  }

  get getLineups() {
    return this.statusesLineup
  }

  get getSeasonMatches() {
    return this.seasonMatches
  }

  get listedTeams() {
    if(this.match && (this.match.teams || []).length > 2) {
      return this.team ? [this.team] : []
    } else if(this.match) {
      return this.match.teams
    }
  }

  get downloadCsvPath() {
    return `/prices/match/${this.match}.csv`
  }

  get downloadSeasonCsvPath() {
    return `/prices/season/${this.season}.csv`
  }

  get uploadSeasonCsvPath() {
    return `/prices/season/${this.season}`
  }

  get uploadCsvPath() {
    return `/prices/match/${this.match}`
  }

  get allowAutomatedPrice() {
    const {sport} = (this.season || {})
    if(!sport) return false
    return ['football', 'cricket', 'basket'].includes(sport)
  }

  get rounds() {
    return (this.matches || []).map(m => m.round)
  }

  get sportRemarks() {
    return {
      hockey: [{
        field: 'line',
        type: 'select',
        options: ',1L,2L,3L,4L'.split(','),
      }, {
        field: 'ppLine',
        type: 'select',
        options: ',1PP,2PP'.split(','),
      }],
      lol: [{
        field: 'role',
        type: 'select',
        options: [
          'top',
          'jungle',
          'middle',
          'bottom',
          'support',
        ],
      }],
      tennis: [{
        field: 'quater',
        type: 'select',
        options: ',Q1,Q2,Q3,Q4'.split(','),
      }],
      formula_one: [{
        field: 'role',
        type: 'select',
        options: [
          'T1',
          'T2',
          'T3',
          'T4',
        ],
      }],
      dota2: [{
        field: 'role',
        type: 'select',
        options: [
          'top',
          'jungle',
          'middle',
          'bottom',
          'support',
        ],
      }],
      mma: [{
        field: 'weightClass',
        type: 'select',
        options: [
          '',
          'straw',
          'fly',
          'bantam',
          'feather',
          'light',
          'superlight',
          'welter',
          'superwelter',
          'middle',
          'supermiddle',
          'lightheavy',
          'cruiser',
          'heavy',
          'superheavy',
        ],
      }],
    }
  }

  get projectionSources() {
    return this.match
      ?.matchSources
      ?.filter(s => sources[s.source]?.projections)
  }

  get priceSources() {
    return this.match
      ?.matchSources
      ?.filter(s => sources[s.source]?.prices)
  }

  async scrape(scope, source) {
    if(source.$isSubmitting) return
    source.$isSubmitting = true
    this.render()

    try {
      await source.post(`scrape-${scope}`)

      notify(`Scraping of ${scope} is completed`, {
        level: 'success',
        timeout: 5000,
      })

      // Reload match data to get updated lineups with projections
      if(scope === 'projections' && this.match) {
        const data = await this.match.load()
        delete data.user
        this.update(data)

        // Rebuild projections map from updated lineups
        const projections = {}
        const lineups = this.match?.lineups || this.lineups || []
        for(const l of lineups) {
          if(!l.fantasyProjection) continue
          projections[l.player] = l.fantasyProjection
        }

        // Reload player stats with new projections
        var teamsparams = Object.assign({
          sport: this.season.sport,
          teams: this.match.teamIds,
        }, [ 'baseball', 'basket' ].includes(this.season.sport) ? {
          league: this.season.league._id,
        } : {
          season: this.season._id,
        })

        const statsData = await Match.load('match-stats', teamsparams)
        statsData.projections = projections
        this.seasonMatches = statsData
        this._loadPlayerStats(statsData)
      }
    } catch(err) {
      notify(err.message, { level: 'danger', timeout: 1000 })
    } finally {
      source.$isSubmitting = false
      this.render()
    }
  }

  get hasRemarks() {
    const sport = this.season && this.season.sport
    return this.sportRemarks[sport]
  }

  get remarks() {
    const sport = this.season && this.season.sport
    const defaultRemarks = [{ field: 'seasonPrice' }]
    if(this.priceScope) {
      return [{
        field: 'priceScope',
        type: 'priceScope',
      }]
    }
    if(this.showRemarks) {
      return this.sportRemarks[sport] || defaultRemarks
    }
    return defaultRemarks
  }

  get isLoading() {
    return this.$reload || this.$changeMatch
  }

  getSourceName(source) {
    const { source: name } = source
    switch(name) {
      case 'fdNationalBasketballAssociation': return 'fdNBA'
      case 'fdNationalHockeyLeague': return 'fdNHL'
      case 'fdAmericanFootball': return 'fdNFL'
      default: return name
    }
  }
}
